"use client";

import Link from "next/link";
import { signIn } from "next-auth/react";
import { useRouter, useSearchParams } from "next/navigation";
import { FormEvent, Suspense, useState } from "react";

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get("callbackUrl") || "/after-login";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setPending(true);
    try {
      const result = await signIn("credentials", {
        email,
        password,
        redirect: false,
      });
      if (result?.error) {
        setError("Invalid email or password.");
        return;
      }
      router.push(callbackUrl);
      router.refresh();
    } catch {
      setError("Couldn’t sign in — try again.");
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="min-h-full bg-ink-50 font-sans">
      <main className="mx-auto flex w-full max-w-md flex-col gap-6 px-6 py-16">
        <div>
          <p className="text-sm font-medium tracking-wide text-ink-500 uppercase">
            Daily15
          </p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-ink-950">
            Sign in
          </h1>
          <p className="mt-2 text-sm text-ink-600">
            Use the email and password for your account.
          </p>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <label className="flex flex-col gap-1 text-sm font-medium text-ink-700">
            Email
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-xl border border-ink-300 bg-white px-4 py-3 text-base font-normal text-ink-950 outline-none ring-brand-500 focus:ring-2"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm font-medium text-ink-700">
            Password
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-xl border border-ink-300 bg-white px-4 py-3 text-base font-normal text-ink-950 outline-none ring-brand-500 focus:ring-2"
            />
          </label>
          {error && <p className="text-sm text-rose-600">{error}</p>}
          <button
            type="submit"
            disabled={pending}
            className="rounded-2xl bg-brand-600 px-5 py-3 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {pending ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p className="text-sm text-ink-600">
          No account yet?{" "}
          <Link
            href="/register"
            className="font-medium text-ink-950 underline-offset-2 hover:underline"
          >
            Create a student account
          </Link>
        </p>
        <p className="text-sm text-ink-600">
          Teacher?{" "}
          <Link
            href="/join"
            className="font-medium text-ink-950 underline-offset-2 hover:underline"
          >
            Join your school
          </Link>
          {" · "}
          <Link
            href="/create-school"
            className="font-medium text-ink-950 underline-offset-2 hover:underline"
          >
            Create a school
          </Link>
        </p>
      </main>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-full bg-ink-50 px-6 py-16 font-sans text-ink-600">
          Loading…
        </div>
      }
    >
      <LoginForm />
    </Suspense>
  );
}
